import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { useIntl } from 'react-intl';
import Tabs from '../../../../../../ui/canon/Tabs';
import InfoTooltip from '../../../common/InfoTooltip';
import { getRevenueLineTerm, getRevenueLineTerminology } from '../../revenueLineTerminology';
import { useCurrency } from '../../../../hooks/useCurrency';
import { exceedsRevenueCap, revenueCapMessage } from '../../../../utils/revenueCap';

const UPDATE_USER_MUTATION = gql`
  mutation UpdateCurrentUser(
    $fee_based_revenue: Float
    $trailer_revenue: Float
    $direct_revenue: Float
    $transactional_revenue: Float
    $year_1_gross: Float
    $year_2_gross: Float
    $year_3_gross: Float
    $year_4_gross: Float
    $year_5_gross: Float
    $li_first_year_commissions: Float
    $li_renewal_commissions: Float
    $li_override_commissions: Float
    $li_other_revenues: Float
    $li_policy_count: Int
    $life_year_1_gross: Float
    $life_year_2_gross: Float
    $life_year_3_gross: Float
    $life_year_4_gross: Float
    $life_year_5_gross: Float
    $pc_personal_lines_premium: Float
    $pc_personal_lines_commission_rate: Float
    $pc_commercial_lines_premium: Float
    $pc_commercial_lines_commission_rate: Float
    $pc_personal_retention_rate: Float
    $pc_commercial_retention_rate: Float
    $pc_year_1_commission: Float
    $pc_year_2_commission: Float
    $pc_year_3_commission: Float
    $pc_year_4_commission: Float
    $pc_year_5_commission: Float
    $group_first_year_commissions: Float
    $group_renewal_commissions: Float
    $group_override_commissions: Float
    $group_other_revenues: Float
    $group_number_of_cases: Int
    $group_average_case_size: Float
    $group_case_retention_rate: Float
    $group_year_1_gross: Float
    $group_year_2_gross: Float
    $group_year_3_gross: Float
    $group_year_4_gross: Float
    $group_year_5_gross: Float
  ) {
    updateCurrentUser(
      fee_based_revenue: $fee_based_revenue
      trailer_revenue: $trailer_revenue
      direct_revenue: $direct_revenue
      transactional_revenue: $transactional_revenue
      year_1_gross: $year_1_gross
      year_2_gross: $year_2_gross
      year_3_gross: $year_3_gross
      year_4_gross: $year_4_gross
      year_5_gross: $year_5_gross
      li_first_year_commissions: $li_first_year_commissions
      li_renewal_commissions: $li_renewal_commissions
      li_override_commissions: $li_override_commissions
      li_other_revenues: $li_other_revenues
      li_policy_count: $li_policy_count
      life_year_1_gross: $life_year_1_gross
      life_year_2_gross: $life_year_2_gross
      life_year_3_gross: $life_year_3_gross
      life_year_4_gross: $life_year_4_gross
      life_year_5_gross: $life_year_5_gross
      pc_personal_lines_premium: $pc_personal_lines_premium
      pc_personal_lines_commission_rate: $pc_personal_lines_commission_rate
      pc_commercial_lines_premium: $pc_commercial_lines_premium
      pc_commercial_lines_commission_rate: $pc_commercial_lines_commission_rate
      pc_personal_retention_rate: $pc_personal_retention_rate
      pc_commercial_retention_rate: $pc_commercial_retention_rate
      pc_year_1_commission: $pc_year_1_commission
      pc_year_2_commission: $pc_year_2_commission
      pc_year_3_commission: $pc_year_3_commission
      pc_year_4_commission: $pc_year_4_commission
      pc_year_5_commission: $pc_year_5_commission
      group_first_year_commissions: $group_first_year_commissions
      group_renewal_commissions: $group_renewal_commissions
      group_override_commissions: $group_override_commissions
      group_other_revenues: $group_other_revenues
      group_number_of_cases: $group_number_of_cases
      group_average_case_size: $group_average_case_size
      group_case_retention_rate: $group_case_retention_rate
      group_year_1_gross: $group_year_1_gross
      group_year_2_gross: $group_year_2_gross
      group_year_3_gross: $group_year_3_gross
      group_year_4_gross: $group_year_4_gross
      group_year_5_gross: $group_year_5_gross
    )
  }
`;

const PULL_REVENUE_INFORMATION = gql`
  mutation pullRevenueInformationForUserModal {
    pullRevenueInformationForUser {
      id
      trailer_revenue
      direct_revenue
      transactional_revenue
      fee_based_revenue
      years_practicing
      number_of_clients
      annual_revenue
      recurring_revenue
      assets_under_management
      avg_client_age
      year_1_gross
      year_2_gross
      year_3_gross
      year_4_gross
      year_5_gross
    }
  }
`;

const UPDATE_AUTO_SYNC = gql`
  mutation updateAutoSyncRevenueModal($updateProductionDataAutomatically: Boolean!) {
    updateCurrentUser(update_production_data_automatically: $updateProductionDataAutomatically)
  }
`;

// Feature flag to line mapping
const LINE_FEATURES = {
  wealth: 'multi_line_valuation_wealth_management',
  life: 'multi_line_valuation_life_insurance',
  pc: 'multi_line_valuation_p_and_c',
  group: 'multi_line_valuation_group',
};

// Current year for historical labels
const CURRENT_YEAR = new Date().getFullYear();

const normalizeNumericInput = (value, allowDecimal = true) => {
  if (value === null || value === undefined) return '';
  const raw = String(value).replace(/,/g, '');
  const cleaned = raw.replace(allowDecimal ? /[^0-9.]/g : /[^0-9]/g, '');

  if (!allowDecimal) return cleaned;

  const firstDotIndex = cleaned.indexOf('.');
  if (firstDotIndex === -1) return cleaned;

  const integerPart = cleaned.slice(0, firstDotIndex);
  const decimalPart = cleaned.slice(firstDotIndex + 1).replace(/\./g, '');
  return `${integerPart}.${decimalPart}`;
};

const formatWithThousandsSeparators = (value, allowDecimal = true) => {
  const normalized = normalizeNumericInput(value, allowDecimal);
  if (normalized === '') return '';

  const hasTrailingDot = allowDecimal && normalized.endsWith('.');
  const [integerRaw, decimalPart] = normalized.split('.');
  const integerPart = integerRaw === '' ? '0' : integerRaw.replace(/^0+(?=\d)/, '');
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  if (!allowDecimal) return formattedInteger;
  if (hasTrailingDot) return `${formattedInteger}.`;
  if (decimalPart !== undefined) return `${formattedInteger}.${decimalPart}`;
  return formattedInteger;
};

const NumericTextInput = ({ value, onChange, allowDecimal = true, className }) => (
  <input
    type="text"
    inputMode={allowDecimal ? 'decimal' : 'numeric'}
    value={formatWithThousandsSeparators(value, allowDecimal)}
    onChange={(e) => onChange(normalizeNumericInput(e.target.value, allowDecimal))}
    className={className}
    style={{ border: 'none', outline: 'none' }}
  />
);

NumericTextInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  allowDecimal: PropTypes.bool,
  className: PropTypes.string.isRequired,
};

// Currency input component - defined outside to prevent re-creation on each render
const CurrencyInput = ({ label, value, onChange, small = false, disabled = false, error = null }) => {
  const { symbol } = useCurrency();
  return (
  <div>
    <label className={`block ${small ? 'text-xs' : 'text-sm'} text-succession-text-primary mb-1`}>{label}</label>
    <div className={`flex items-center border ${error ? 'border-red-500' : 'border-succession-border'} rounded ${disabled ? 'bg-succession-bg-secondary opacity-60' : 'bg-succession-bg-primary'}`}>
      <span className={`${small ? 'px-2 text-sm' : 'px-3'} text-succession-text-primary select-none`}>{symbol}</span>
      <NumericTextInput
        value={value}
        onChange={onChange}
        disabled={disabled}
        className={`flex-1 ${small ? 'pr-2 py-1 text-sm' : 'pr-3 py-2'} bg-transparent text-succession-text-primary border-0 outline-none disabled:cursor-not-allowed`}
        style={{ border: 'none', outline: 'none' }}
      />
    </div>
    {error && <p className={`mt-1 ${small ? 'text-xs' : 'text-sm'} text-red-600`}>{error}</p>}
  </div>
  );
};

CurrencyInput.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  tooltip: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  small: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.string,
};

// Percent input component - defined outside to prevent re-creation on each render
const PercentInput = ({ label, value, onChange, disabled = false }) => (
  <div>
    <label className="block text-sm text-succession-text-primary mb-1">{label}</label>
    <div className={`flex items-center border border-succession-border rounded ${disabled ? 'bg-succession-bg-secondary opacity-60' : 'bg-succession-bg-primary'}`}>
      <input
        type="number"
        step="0.1"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="flex-1 px-3 py-2 bg-transparent text-succession-text-primary border-0 outline-none disabled:cursor-not-allowed"
        style={{ border: 'none', outline: 'none' }}
      />
      <span className="px-3 text-succession-text-primary select-none">%</span>
    </div>
  </div>
);

PercentInput.propTypes = {
  label: PropTypes.string.isRequired,
  tooltip: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

// Number input component - defined outside to prevent re-creation on each render
const NumberInput = ({ label, value, onChange, disabled = false }) => (
  <div>
    <label className="block text-sm text-succession-text-primary mb-1">{label}</label>
    <div className={`flex items-center border border-succession-border rounded ${disabled ? 'bg-succession-bg-secondary opacity-60' : 'bg-succession-bg-primary'}`}>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="flex-1 px-3 py-2 bg-transparent text-succession-text-primary border-0 outline-none disabled:cursor-not-allowed"
        style={{ border: 'none', outline: 'none' }}
      />
    </div>
  </div>
);

NumberInput.propTypes = {
  label: PropTypes.string.isRequired,
  tooltip: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

const RevenueInformationEditModal = ({ user, onClose, onSave, onError, title = 'Edit Revenue Information', submitButtonText = 'Save Changes', submitButtonIcon = null, isReadOnly = false, companyName = 'your firm', quickbooksConnected = false }) => {
  const intl = useIntl();
  const { symbol, format: formatCurrency } = useCurrency();
  const resolvedTitle = title || intl.formatMessage({ id: 'succession.profile_edit.modals.revenue_information_edit.title' });
  const resolvedSubmitButtonText = submitButtonText || intl.formatMessage({ id: 'succession.profile_edit.modals.revenue_information_edit.save_changes' });

  const featureTypes = user?.company?.feature_types || [];
  const showPullButton = featureTypes.includes('show_pull_latest_revenue_button');
  const showAutoSync = featureTypes.includes('users_choice_update_production_data_automatically');

  // Sync state
  const [syncConfirmOpen, setSyncConfirmOpen] = useState(false);
  const [autoSyncConfirmOpen, setAutoSyncConfirmOpen] = useState(false);
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(!!user?.update_production_data_automatically);
  const [syncSuccess, setSyncSuccess] = useState(false);

  const [syncError, setSyncError] = useState(null);

  const [pullRevenue, { loading: pullLoading }] = useMutation(PULL_REVENUE_INFORMATION, {
    refetchQueries: ['GetCurrentUserProfile', 'getCurrentUserForMultiLine'],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      if (!data?.pullRevenueInformationForUser) {
        setSyncConfirmOpen(false);
        setSyncError('Sync failed. Please try again or contact support.');
        return;
      }
      onSave('Revenue data synced successfully');
    },
    onError: (err) => {
      console.error('Revenue sync error:', err);
      setSyncConfirmOpen(false);
      setSyncError(err.message || 'Sync failed. Please try again.');
    },
  });

  const [updateAutoSync] = useMutation(UPDATE_AUTO_SYNC, {
    onError: (err) => console.error('Auto-sync update error:', err),
  });

  const handleToggleAutoSync = (newValue) => {
    if (newValue) {
      setAutoSyncEnabled(true);
      setAutoSyncConfirmOpen(true);
    } else {
      setAutoSyncEnabled(false);
      updateAutoSync({ variables: { updateProductionDataAutomatically: false } });
    }
  };

  const handleConfirmAutoSync = () => {
    setAutoSyncConfirmOpen(false);
    updateAutoSync({ variables: { updateProductionDataAutomatically: true } });
  };

  const handleCancelAutoSync = () => {
    setAutoSyncEnabled(false);
    setAutoSyncConfirmOpen(false);
  };
  const terminology = getRevenueLineTerminology(user?.company?.revenue_line_terminology);

  // Per-tenant ceiling on any single revenue field (Company#max_revenue_input; null = no cap).
  // Same rule as onboarding and the server-side check in updateCurrentUser.
  const maxRevenue = user?.company?.max_revenue_input;
  const [capSubmitError, setCapSubmitError] = useState(null);
  // Already-saved values are left alone so a lowered cap doesn't lock the rest of the form.
  const capError = (value, savedValue) => {
    if (isReadOnly || Number(value) === Number(savedValue || 0)) return null;
    return exceedsRevenueCap(value, maxRevenue) ? revenueCapMessage(maxRevenue) : null;
  };

  // Determine which tabs to show based on feature flags
  const enabledLines = useMemo(() => {
    const lines = [];
    Object.entries(LINE_FEATURES).forEach(([line, feature]) => {
      if (featureTypes.includes(feature)) {
        lines.push(line);
      }
    });
    // If no multi-line features enabled, default to wealth only
    return lines.length > 0 ? lines : ['wealth'];
  }, [featureTypes]);

  const LINE_LABELS = useMemo(() => ({
    wealth: intl.formatMessage({ id: 'succession.profile_edit.modals.revenue_information_edit.line_label_wealth' }),
    life: intl.formatMessage({ id: 'succession.profile_edit.modals.revenue_information_edit.line_label_life' }),
    pc: intl.formatMessage({ id: 'succession.profile_edit.modals.revenue_information_edit.line_label_pc' }),
    group: intl.formatMessage({ id: 'succession.profile_edit.modals.revenue_information_edit.line_label_group' }),
  }), [intl]);

  const [activeTab, setActiveTab] = useState(enabledLines[0] || 'wealth');

  // Wealth state
  const [feeBasedRevenue, setFeeBasedRevenue] = useState(user.fee_based_revenue || 0);
  const [trailerRevenue, setTrailerRevenue] = useState(user.trailer_revenue || 0);
  const [directRevenue, setDirectRevenue] = useState(user.direct_revenue || 0);
  const [transactionalRevenue, setTransactionalRevenue] = useState(user.transactional_revenue || 0);
  const [year1Gross, setYear1Gross] = useState(user.year_1_gross || 0);
  const [year2Gross, setYear2Gross] = useState(user.year_2_gross || 0);
  const [year3Gross, setYear3Gross] = useState(user.year_3_gross || 0);
  const [year4Gross, setYear4Gross] = useState(user.year_4_gross || 0);
  const [year5Gross, setYear5Gross] = useState(user.year_5_gross || 0);

  // Life state
  const [liFirstYearCommissions, setLiFirstYearCommissions] = useState(user.li_first_year_commissions || 0);
  const [liRenewalCommissions, setLiRenewalCommissions] = useState(user.li_renewal_commissions || 0);
  const [liOverrideCommissions, setLiOverrideCommissions] = useState(user.li_override_commissions || 0);
  const [liOtherRevenues, setLiOtherRevenues] = useState(user.li_other_revenues || 0);
  const [liPolicyCount, setLiPolicyCount] = useState(user.li_policy_count || 0);
  const [lifeYear1Gross, setLifeYear1Gross] = useState(user.life_year_1_gross || 0);
  const [lifeYear2Gross, setLifeYear2Gross] = useState(user.life_year_2_gross || 0);
  const [lifeYear3Gross, setLifeYear3Gross] = useState(user.life_year_3_gross || 0);
  const [lifeYear4Gross, setLifeYear4Gross] = useState(user.life_year_4_gross || 0);
  const [lifeYear5Gross, setLifeYear5Gross] = useState(user.life_year_5_gross || 0);

  // P&C state
  const [pcPersonalLinesPremium, setPcPersonalLinesPremium] = useState(user.pc_personal_lines_premium || 0);
  const [pcPersonalLinesCommissionRate, setPcPersonalLinesCommissionRate] = useState(
    (user.pc_personal_lines_commission_rate || 0) * 100
  );
  const [pcCommercialLinesPremium, setPcCommercialLinesPremium] = useState(user.pc_commercial_lines_premium || 0);
  const [pcCommercialLinesCommissionRate, setPcCommercialLinesCommissionRate] = useState(
    (user.pc_commercial_lines_commission_rate || 0) * 100
  );
  const [pcPersonalRetentionRate, setPcPersonalRetentionRate] = useState(
    (user.pc_personal_retention_rate || 0) * 100
  );
  const [pcCommercialRetentionRate, setPcCommercialRetentionRate] = useState(
    (user.pc_commercial_retention_rate || 0) * 100
  );
  const [pcYear1Commission, setPcYear1Commission] = useState(user.pc_year_1_commission || 0);
  const [pcYear2Commission, setPcYear2Commission] = useState(user.pc_year_2_commission || 0);
  const [pcYear3Commission, setPcYear3Commission] = useState(user.pc_year_3_commission || 0);
  const [pcYear4Commission, setPcYear4Commission] = useState(user.pc_year_4_commission || 0);
  const [pcYear5Commission, setPcYear5Commission] = useState(user.pc_year_5_commission || 0);

  // Group state
  const [groupFirstYearCommissions, setGroupFirstYearCommissions] = useState(user.group_first_year_commissions || 0);
  const [groupRenewalCommissions, setGroupRenewalCommissions] = useState(user.group_renewal_commissions || 0);
  const [groupOverrideCommissions, setGroupOverrideCommissions] = useState(user.group_override_commissions || 0);
  const [groupOtherRevenues, setGroupOtherRevenues] = useState(user.group_other_revenues || 0);
  const [groupNumberOfCases, setGroupNumberOfCases] = useState(user.group_number_of_cases || 0);
  const [groupAverageCaseSize, setGroupAverageCaseSize] = useState(user.group_average_case_size || 0);
  const [groupCaseRetentionRate, setGroupCaseRetentionRate] = useState(
    (user.group_case_retention_rate || 0) * 100
  );
  const [groupYear1Gross, setGroupYear1Gross] = useState(user.group_year_1_gross || 0);
  const [groupYear2Gross, setGroupYear2Gross] = useState(user.group_year_2_gross || 0);
  const [groupYear3Gross, setGroupYear3Gross] = useState(user.group_year_3_gross || 0);
  const [groupYear4Gross, setGroupYear4Gross] = useState(user.group_year_4_gross || 0);
  const [groupYear5Gross, setGroupYear5Gross] = useState(user.group_year_5_gross || 0);

  // Computed values for display
  const [wealthGrossProduction, setWealthGrossProduction] = useState(0);
  const [wealthRecurringRevenue, setWealthRecurringRevenue] = useState(0);

  const [updateUser, { loading }] = useMutation(UPDATE_USER_MUTATION, {
    onCompleted: () => onSave('Revenue information updated successfully'),
    onError: (error) => onError(error),
    refetchQueries: ['GetCurrentUserProfile', 'getCurrentUserForMultiLine'],
    awaitRefetchQueries: true,
  });

  // Calculate Wealth computed values
  useEffect(() => {
    const gross =
      parseFloat(feeBasedRevenue) +
      parseFloat(trailerRevenue) +
      parseFloat(directRevenue) +
      parseFloat(transactionalRevenue);
    const recurring = parseFloat(feeBasedRevenue) + parseFloat(trailerRevenue) + parseFloat(directRevenue);
    setWealthGrossProduction(gross);
    setWealthRecurringRevenue(recurring);
  }, [feeBasedRevenue, trailerRevenue, directRevenue, transactionalRevenue]);

  const parseFloatOrNull = (value) => {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? null : parsed;
  };

  const parseIntOrNull = (value) => {
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? null : parsed;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // When read-only, bypass mutation and proceed directly (e.g. to Show Valuation)
    if (isReadOnly) {
      onSave('');
      return;
    }

    // Check every line, not just the visible tab.
    const cappedFields = [
      [feeBasedRevenue, user.fee_based_revenue],
      [trailerRevenue, user.trailer_revenue],
      [directRevenue, user.direct_revenue],
      [transactionalRevenue, user.transactional_revenue],
      [year1Gross, user.year_1_gross],
      [year2Gross, user.year_2_gross],
      [year3Gross, user.year_3_gross],
      [year4Gross, user.year_4_gross],
      [year5Gross, user.year_5_gross],
      [liFirstYearCommissions, user.li_first_year_commissions],
      [liRenewalCommissions, user.li_renewal_commissions],
      [liOverrideCommissions, user.li_override_commissions],
      [liOtherRevenues, user.li_other_revenues],
      [pcPersonalLinesPremium, user.pc_personal_lines_premium],
      [pcCommercialLinesPremium, user.pc_commercial_lines_premium],
      [groupFirstYearCommissions, user.group_first_year_commissions],
      [groupRenewalCommissions, user.group_renewal_commissions],
      [groupOverrideCommissions, user.group_override_commissions],
      [groupOtherRevenues, user.group_other_revenues],
      [groupAverageCaseSize, user.group_average_case_size],
    ];
    if (cappedFields.some(([value, saved]) => capError(value, saved))) {
      setCapSubmitError(revenueCapMessage(maxRevenue));
      return;
    }
    setCapSubmitError(null);

    const variables = {
      // Wealth fields
      fee_based_revenue: parseFloatOrNull(feeBasedRevenue),
      trailer_revenue: parseFloatOrNull(trailerRevenue),
      direct_revenue: parseFloatOrNull(directRevenue),
      transactional_revenue: parseFloatOrNull(transactionalRevenue),
      year_1_gross: parseFloatOrNull(year1Gross),
      year_2_gross: parseFloatOrNull(year2Gross),
      year_3_gross: parseFloatOrNull(year3Gross),
      year_4_gross: parseFloatOrNull(year4Gross),
      year_5_gross: parseFloatOrNull(year5Gross),
      // Life fields
      li_first_year_commissions: parseFloatOrNull(liFirstYearCommissions),
      li_renewal_commissions: parseFloatOrNull(liRenewalCommissions),
      li_override_commissions: parseFloatOrNull(liOverrideCommissions),
      li_other_revenues: parseFloatOrNull(liOtherRevenues),
      li_policy_count: parseIntOrNull(liPolicyCount),
      life_year_1_gross: parseFloatOrNull(lifeYear1Gross),
      life_year_2_gross: parseFloatOrNull(lifeYear2Gross),
      life_year_3_gross: parseFloatOrNull(lifeYear3Gross),
      life_year_4_gross: parseFloatOrNull(lifeYear4Gross),
      life_year_5_gross: parseFloatOrNull(lifeYear5Gross),
      // P&C fields (rates sent as percent, normalized server-side)
      pc_personal_lines_premium: parseFloatOrNull(pcPersonalLinesPremium),
      pc_personal_lines_commission_rate: parseFloatOrNull(pcPersonalLinesCommissionRate),
      pc_commercial_lines_premium: parseFloatOrNull(pcCommercialLinesPremium),
      pc_commercial_lines_commission_rate: parseFloatOrNull(pcCommercialLinesCommissionRate),
      pc_personal_retention_rate: parseFloatOrNull(pcPersonalRetentionRate),
      pc_commercial_retention_rate: parseFloatOrNull(pcCommercialRetentionRate),
      pc_year_1_commission: parseFloatOrNull(pcYear1Commission),
      pc_year_2_commission: parseFloatOrNull(pcYear2Commission),
      pc_year_3_commission: parseFloatOrNull(pcYear3Commission),
      pc_year_4_commission: parseFloatOrNull(pcYear4Commission),
      pc_year_5_commission: parseFloatOrNull(pcYear5Commission),
      // Group fields
      group_first_year_commissions: parseFloatOrNull(groupFirstYearCommissions),
      group_renewal_commissions: parseFloatOrNull(groupRenewalCommissions),
      group_override_commissions: parseFloatOrNull(groupOverrideCommissions),
      group_other_revenues: parseFloatOrNull(groupOtherRevenues),
      group_number_of_cases: parseIntOrNull(groupNumberOfCases),
      group_average_case_size: parseFloatOrNull(groupAverageCaseSize),
      group_case_retention_rate: parseFloatOrNull(groupCaseRetentionRate),
      group_year_1_gross: parseFloatOrNull(groupYear1Gross),
      group_year_2_gross: parseFloatOrNull(groupYear2Gross),
      group_year_3_gross: parseFloatOrNull(groupYear3Gross),
      group_year_4_gross: parseFloatOrNull(groupYear4Gross),
      group_year_5_gross: parseFloatOrNull(groupYear5Gross),
    };

    updateUser({ variables });
  };

  const tabs = enabledLines.map((line) => ({
    id: line,
    label: LINE_LABELS[line],
  }));

  // Historical year labels
  const yearLabels = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2, CURRENT_YEAR - 3, CURRENT_YEAR - 4];

  // Render Wealth tab
  const renderWealthTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-succession-text-primary mb-3">{intl.formatMessage({ id: 'succession.profile_edit.modals.revenue_information_edit.revenue_breakdown' })}</h3>
        <div className="grid grid-cols-2 gap-4">
          <CurrencyInput label={getRevenueLineTerm(terminology, 'wealth', 'fee_based_revenue').label} tooltip={getRevenueLineTerm(terminology, 'wealth', 'fee_based_revenue').definition} value={feeBasedRevenue} error={capError(feeBasedRevenue, user.fee_based_revenue)} onChange={setFeeBasedRevenue} />
          <CurrencyInput label={getRevenueLineTerm(terminology, 'wealth', 'trailer_revenue').label} tooltip={getRevenueLineTerm(terminology, 'wealth', 'trailer_revenue').definition} value={trailerRevenue} error={capError(trailerRevenue, user.trailer_revenue)} onChange={setTrailerRevenue} />
          <CurrencyInput label={getRevenueLineTerm(terminology, 'wealth', 'direct_revenue').label} tooltip={getRevenueLineTerm(terminology, 'wealth', 'direct_revenue').definition} value={directRevenue} error={capError(directRevenue, user.direct_revenue)} onChange={setDirectRevenue} />
          <CurrencyInput label={getRevenueLineTerm(terminology, 'wealth', 'transactional_revenue').label} tooltip={getRevenueLineTerm(terminology, 'wealth', 'transactional_revenue').definition} value={transactionalRevenue} error={capError(transactionalRevenue, user.transactional_revenue)} onChange={setTransactionalRevenue} />
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-succession-text-primary mb-3">{intl.formatMessage({ id: 'succession.profile_edit.modals.revenue_information_edit.historical_gross_production' })}</h3>
        <div className="grid grid-cols-5 gap-3">
          <CurrencyInput label={yearLabels[0]} value={year1Gross} error={capError(year1Gross, user.year_1_gross)} onChange={setYear1Gross} small disabled={isReadOnly} />
          <CurrencyInput label={yearLabels[1]} value={year2Gross} error={capError(year2Gross, user.year_2_gross)} onChange={setYear2Gross} small disabled={isReadOnly} />
          <CurrencyInput label={yearLabels[2]} value={year3Gross} error={capError(year3Gross, user.year_3_gross)} onChange={setYear3Gross} small disabled={isReadOnly} />
          <CurrencyInput label={yearLabels[3]} value={year4Gross} error={capError(year4Gross, user.year_4_gross)} onChange={setYear4Gross} small disabled={isReadOnly} />
          <CurrencyInput label={yearLabels[4]} value={year5Gross} error={capError(year5Gross, user.year_5_gross)} onChange={setYear5Gross} small disabled={isReadOnly} />
        </div>
      </div>

      <div className="bg-succession-bg-primary p-4 rounded border border-succession-border">
        <h3 className="text-sm font-semibold text-succession-text-primary mb-2">{intl.formatMessage({ id: 'succession.profile_edit.modals.revenue_information_edit.calculated_values' })}</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-succession-text-muted">{intl.formatMessage({ id: 'succession.profile_edit.modals.revenue_information_edit.gross_production' })}</span>
            <p className="font-semibold text-succession-text-primary">{formatCurrency(wealthGrossProduction, { maximumFractionDigits: 0 })}</p>
          </div>
          <div>
            <span className="text-succession-text-muted">{intl.formatMessage({ id: 'succession.profile_edit.modals.revenue_information_edit.recurring_revenue' })}</span>
            <p className="font-semibold text-succession-text-primary">{formatCurrency(wealthRecurringRevenue, { maximumFractionDigits: 0 })}</p>
          </div>
        </div>
      </div>
    </div>
  );

  // Render Life tab
  const renderLifeTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-succession-text-primary mb-3">{intl.formatMessage({ id: 'succession.profile_edit.modals.revenue_information_edit.life_revenue_information' })}</h3>
        <div className="grid grid-cols-2 gap-4">
          <CurrencyInput
            label={intl.formatMessage({ id: 'succession.profile_edit.modals.revenue_information_edit.first_year_commissions' })}
            value={liFirstYearCommissions} error={capError(liFirstYearCommissions, user.li_first_year_commissions)}
            onChange={setLiFirstYearCommissions}
            disabled={isReadOnly}
          />
          <CurrencyInput
            label={intl.formatMessage({ id: 'succession.profile_edit.modals.revenue_information_edit.renewal_commissions' })}
            value={liRenewalCommissions} error={capError(liRenewalCommissions, user.li_renewal_commissions)}
            onChange={setLiRenewalCommissions}
            disabled={isReadOnly}
          />
          <CurrencyInput
            label={intl.formatMessage({ id: 'succession.profile_edit.modals.revenue_information_edit.override_commissions' })}
            value={liOverrideCommissions} error={capError(liOverrideCommissions, user.li_override_commissions)}
            onChange={setLiOverrideCommissions}
            disabled={isReadOnly}
          />
          <CurrencyInput label={getRevenueLineTerm(terminology, 'life', 'other_revenues').label} tooltip={getRevenueLineTerm(terminology, 'life', 'other_revenues').definition} value={liOtherRevenues} error={capError(liOtherRevenues, user.li_other_revenues)} onChange={setLiOtherRevenues} />
          <NumberInput label={intl.formatMessage({ id: 'succession.profile_edit.modals.revenue_information_edit.policy_count' })} value={liPolicyCount} onChange={setLiPolicyCount} />
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-succession-text-primary mb-3">{intl.formatMessage({ id: 'succession.profile_edit.modals.revenue_information_edit.historical_gross_production' })}</h3>
        <div className="grid grid-cols-5 gap-3">
          <CurrencyInput label={yearLabels[0]} value={lifeYear1Gross} onChange={setLifeYear1Gross} small disabled={isReadOnly} />
          <CurrencyInput label={yearLabels[1]} value={lifeYear2Gross} onChange={setLifeYear2Gross} small disabled={isReadOnly} />
          <CurrencyInput label={yearLabels[2]} value={lifeYear3Gross} onChange={setLifeYear3Gross} small disabled={isReadOnly} />
          <CurrencyInput label={yearLabels[3]} value={lifeYear4Gross} onChange={setLifeYear4Gross} small disabled={isReadOnly} />
          <CurrencyInput label={yearLabels[4]} value={lifeYear5Gross} onChange={setLifeYear5Gross} small disabled={isReadOnly} />
        </div>
      </div>
    </div>
  );

  // Render P&C tab
  const renderPCTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-succession-text-primary mb-3">{intl.formatMessage({ id: 'succession.profile_edit.modals.revenue_information_edit.personal_lines' })}</h3>
        <div className="grid grid-cols-3 gap-4">
          <CurrencyInput
            label={getRevenueLineTerm(terminology, 'pc', 'premium').label}
            tooltip={getRevenueLineTerm(terminology, 'pc', 'premium').definition}
            value={pcPersonalLinesPremium} error={capError(pcPersonalLinesPremium, user.pc_personal_lines_premium)}
            onChange={setPcPersonalLinesPremium}
            disabled={isReadOnly}
          />
          <PercentInput
            label={getRevenueLineTerm(terminology, 'pc', 'commission_rate').label}
            tooltip={getRevenueLineTerm(terminology, 'pc', 'commission_rate').definition}
            value={pcPersonalLinesCommissionRate}
            onChange={setPcPersonalLinesCommissionRate}
            disabled={isReadOnly}
          />
          <PercentInput
            label={getRevenueLineTerm(terminology, 'pc', 'retention_rate').label}
            tooltip={getRevenueLineTerm(terminology, 'pc', 'retention_rate').definition}
            value={pcPersonalRetentionRate}
            onChange={setPcPersonalRetentionRate}
            disabled={isReadOnly}
          />
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-succession-text-primary mb-3">{intl.formatMessage({ id: 'succession.profile_edit.modals.revenue_information_edit.commercial_lines' })}</h3>
        <div className="grid grid-cols-3 gap-4">
          <CurrencyInput
            label={getRevenueLineTerm(terminology, 'pc', 'premium').label}
            tooltip={getRevenueLineTerm(terminology, 'pc', 'premium').definition}
            value={pcCommercialLinesPremium} error={capError(pcCommercialLinesPremium, user.pc_commercial_lines_premium)}
            onChange={setPcCommercialLinesPremium}
            disabled={isReadOnly}
          />
          <PercentInput
            label={getRevenueLineTerm(terminology, 'pc', 'commission_rate').label}
            tooltip={getRevenueLineTerm(terminology, 'pc', 'commission_rate').definition}
            value={pcCommercialLinesCommissionRate}
            onChange={setPcCommercialLinesCommissionRate}
            disabled={isReadOnly}
          />
          <PercentInput
            label={getRevenueLineTerm(terminology, 'pc', 'retention_rate').label}
            tooltip={getRevenueLineTerm(terminology, 'pc', 'retention_rate').definition}
            value={pcCommercialRetentionRate}
            onChange={setPcCommercialRetentionRate}
            disabled={isReadOnly}
          />
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-succession-text-primary mb-3">{intl.formatMessage({ id: 'succession.profile_edit.modals.revenue_information_edit.historical_commissions' })}</h3>
        <div className="grid grid-cols-5 gap-3">
          <CurrencyInput label={yearLabels[0]} value={pcYear1Commission} onChange={setPcYear1Commission} small disabled={isReadOnly} />
          <CurrencyInput label={yearLabels[1]} value={pcYear2Commission} onChange={setPcYear2Commission} small disabled={isReadOnly} />
          <CurrencyInput label={yearLabels[2]} value={pcYear3Commission} onChange={setPcYear3Commission} small disabled={isReadOnly} />
          <CurrencyInput label={yearLabels[3]} value={pcYear4Commission} onChange={setPcYear4Commission} small disabled={isReadOnly} />
          <CurrencyInput label={yearLabels[4]} value={pcYear5Commission} onChange={setPcYear5Commission} small disabled={isReadOnly} />
        </div>
      </div>
    </div>
  );

  // Render Group tab
  const renderGroupTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-succession-text-primary mb-3">{intl.formatMessage({ id: 'succession.profile_edit.modals.revenue_information_edit.group_revenue_information' })}</h3>
        <div className="grid grid-cols-2 gap-4">
          <CurrencyInput
            label={getRevenueLineTerm(terminology, 'group', 'first_year_commissions').label}
            tooltip={getRevenueLineTerm(terminology, 'group', 'first_year_commissions').definition}
            value={groupFirstYearCommissions} error={capError(groupFirstYearCommissions, user.group_first_year_commissions)}
            onChange={setGroupFirstYearCommissions}
            disabled={isReadOnly}
          />
          <CurrencyInput
            label={getRevenueLineTerm(terminology, 'group', 'renewal_commissions').label}
            tooltip={getRevenueLineTerm(terminology, 'group', 'renewal_commissions').definition}
            value={groupRenewalCommissions} error={capError(groupRenewalCommissions, user.group_renewal_commissions)}
            onChange={setGroupRenewalCommissions}
            disabled={isReadOnly}
          />
          <CurrencyInput
            label={getRevenueLineTerm(terminology, 'group', 'override_commissions').label}
            tooltip={getRevenueLineTerm(terminology, 'group', 'override_commissions').definition}
            value={groupOverrideCommissions} error={capError(groupOverrideCommissions, user.group_override_commissions)}
            onChange={setGroupOverrideCommissions}
            disabled={isReadOnly}
          />
          <CurrencyInput label={getRevenueLineTerm(terminology, 'group', 'other_revenues').label} tooltip={getRevenueLineTerm(terminology, 'group', 'other_revenues').definition} value={groupOtherRevenues} error={capError(groupOtherRevenues, user.group_other_revenues)} onChange={setGroupOtherRevenues} />
          <NumberInput label={intl.formatMessage({ id: 'succession.profile_edit.modals.revenue_information_edit.number_of_cases' })}  value={groupNumberOfCases} onChange={setGroupNumberOfCases} />
          <CurrencyInput label={intl.formatMessage({ id: 'succession.profile_edit.modals.revenue_information_edit.average_case_size' })}value={groupAverageCaseSize} error={capError(groupAverageCaseSize, user.group_average_case_size)} onChange={setGroupAverageCaseSize} />
          <PercentInput
            label={intl.formatMessage({ id: 'succession.profile_edit.modals.revenue_information_edit.case_retention_rate' })}
            value={groupCaseRetentionRate}
            onChange={setGroupCaseRetentionRate}
            disabled={isReadOnly}
          />
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-succession-text-primary mb-3">{intl.formatMessage({ id: 'succession.profile_edit.modals.revenue_information_edit.historical_gross_production' })}</h3>
        <div className="grid grid-cols-5 gap-3">
          <CurrencyInput label={yearLabels[0]} value={groupYear1Gross} onChange={setGroupYear1Gross} small disabled={isReadOnly} />
          <CurrencyInput label={yearLabels[1]} value={groupYear2Gross} onChange={setGroupYear2Gross} small disabled={isReadOnly} />
          <CurrencyInput label={yearLabels[2]} value={groupYear3Gross} onChange={setGroupYear3Gross} small disabled={isReadOnly} />
          <CurrencyInput label={yearLabels[3]} value={groupYear4Gross} onChange={setGroupYear4Gross} small disabled={isReadOnly} />
          <CurrencyInput label={yearLabels[4]} value={groupYear5Gross} onChange={setGroupYear5Gross} small disabled={isReadOnly} />
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'wealth':
        return renderWealthTab();
      case 'life':
        return renderLifeTab();
      case 'pc':
        return renderPCTab();
      case 'group':
        return renderGroupTab();
      default:
        return renderWealthTab();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-50 py-10 overflow-y-auto" onClick={onClose}>
      <div
        className="bg-succession-bg-secondary rounded-lg p-6 max-w-3xl w-full mx-4 border border-succession-border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-succession-text-primary">{title}</h2>
            {quickbooksConnected && (
              <div className="flex items-center gap-1.5 mt-1">
                <img
                  src="https://findbob-production.s3.us-west-2.amazonaws.com/images/qbo_logomark.png"
                  alt="QuickBooks"
                  className="w-4 h-4 object-contain"
                />
                <span className="text-xs text-succession-text-muted">Pre-filled from QuickBooks Online</span>
              </div>
            )}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-succession-bg-primary rounded-full transition-colors">
            <svg className="w-6 h-6 text-succession-text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {enabledLines.length > 1 && (
          <Tabs tabs={tabs} value={activeTab} onChange={setActiveTab} showDescription={false} />
        )}

        <form onSubmit={handleSubmit} className="mt-4">
          {/* Read-only banner — shown when firm manages data */}
          {isReadOnly && (
            <div className="mb-3 p-3 rounded-lg bg-succession-warning/10 border border-succession-warning/30">
              <div className="flex items-start gap-3">
                <svg className="w-4 h-4 text-succession-warning shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <p className="text-sm text-succession-text-primary">
                  Your revenue information is managed by <strong>{companyName}</strong> and updated automatically.
                  {' '}You can view your data here for reference, conduct scenario analysis in the valuation studio, but changes cannot be saved to your profile.
                </p>
              </div>
            </div>
          )}

          {/* Sync controls — shown whenever feature flags are enabled, regardless of read-only */}
          {(showPullButton || showAutoSync) && (
            <div className="mb-4 flex flex-wrap items-center gap-3 pb-4 border-b border-succession-border/50">
              {showPullButton && (
                <button
                  type="button"
                  onClick={() => { setSyncError(null); !syncSuccess && setSyncConfirmOpen(true); }}
                  disabled={pullLoading}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border transition-all duration-300 ${
                    syncSuccess
                      ? 'border-succession-success/50 bg-succession-success/10 text-succession-success'
                      : 'border-succession-border text-succession-text-primary hover:bg-succession-bg-primary disabled:opacity-60 disabled:cursor-not-allowed'
                  }`}
                >
                  {pullLoading ? (
                    <>
                      <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Syncing…
                    </>
                  ) : syncSuccess ? (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                      Synced!
                    </>
                  ) : (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Sync Latest Data
                    </>
                  )}
                </button>
              )}

              {showAutoSync && (
                <label className="inline-flex items-center gap-2.5 cursor-pointer select-none">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={autoSyncEnabled}
                    onClick={() => handleToggleAutoSync(!autoSyncEnabled)}
                    className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-succession-success focus-visible:ring-offset-1 ${
                      autoSyncEnabled ? 'bg-succession-success' : 'bg-succession-border'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm ring-0 transition-transform duration-200 ${
                        autoSyncEnabled ? 'translate-x-4' : 'translate-x-0'
                      }`}
                    />
                  </button>
                  <span className="text-sm text-succession-text-secondary">Auto-sync daily</span>
                </label>
              )}
            </div>
          )}

          {syncError && (
            <div className="mb-4 flex items-center gap-2 px-3 py-2 rounded-lg bg-succession-error/10 border border-succession-error/30 text-sm text-succession-error">
              <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {syncError}
            </div>
          )}

          {renderTabContent()}

          {capSubmitError && (
            <p className="mt-4 text-sm text-red-600" role="alert">
              {capSubmitError}
              {enabledLines.length > 1 && ' — check each revenue tab.'}
            </p>
          )}

          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-succession-border mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-succession-border rounded-lg text-succession-text-primary hover:bg-succession-bg-primary transition-colors"
            >
              {isReadOnly ? intl.formatMessage({ id: 'succession.profile_edit.common.close' }) : intl.formatMessage({ id: 'succession.profile_edit.common.cancel' })}
            </button>
            <div className="relative group">
              <button
                type="submit"
                disabled={loading || (isReadOnly && submitButtonText === 'Save Changes')}
                className={`px-4 py-2 rounded-lg text-white transition-colors inline-flex items-center gap-2 ${
                  (loading || (isReadOnly && submitButtonText === 'Save Changes'))
                    ? 'bg-succession-success opacity-50 cursor-not-allowed'
                    : 'bg-succession-success hover:opacity-80'
                }`}
              >
                {loading ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {intl.formatMessage({ id: 'succession.profile_edit.modals.revenue_information_edit.processing' })}
                  </>
                ) : (
                  <>
                    {submitButtonIcon}
                    {submitButtonText}
                    {submitButtonText !== 'Save Changes' && (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    )}
                  </>
                )}
              </button>
              {isReadOnly && submitButtonText === 'Save Changes' && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-succession-text-primary rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                  Managed by {companyName}
                </div>
              )}
            </div>
          </div>
        </form>
      </div>

      {/* Sync confirmation dialog */}
      {syncConfirmOpen && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50"
          onClick={() => setSyncConfirmOpen(false)}
        >
          <div
            className="bg-succession-bg-secondary border border-succession-border rounded-lg shadow-xl p-6 max-w-sm w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2.5 mb-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-succession-success/10 flex items-center justify-center">
                <svg className="w-4 h-4 text-succession-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <h3 className="text-base font-semibold text-succession-text-primary">Sync Production Data</h3>
            </div>
            <p className="text-sm text-succession-text-secondary mb-5 leading-relaxed">
              This will pull the most recent data from your firm and overwrite any manually entered revenue information. Do you wish to proceed?
            </p>
            <div className="flex gap-2.5 justify-end">
              <button
                type="button"
                onClick={() => setSyncConfirmOpen(false)}
                className="px-3.5 py-1.5 text-sm border border-succession-border rounded-lg text-succession-text-primary hover:bg-succession-bg-primary transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => pullRevenue()}
                disabled={pullLoading}
                className="px-3.5 py-1.5 text-sm bg-succession-success text-white rounded-lg hover:opacity-80 transition-opacity disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                {pullLoading ? (
                  <>
                    <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Syncing…
                  </>
                ) : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Auto-sync confirmation dialog */}
      {autoSyncConfirmOpen && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50"
          onClick={handleCancelAutoSync}
        >
          <div
            className="bg-succession-bg-secondary border border-succession-border rounded-lg shadow-xl p-6 max-w-sm w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2.5 mb-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-succession-warning/10 flex items-center justify-center">
                <svg className="w-4 h-4 text-succession-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-base font-semibold text-succession-text-primary">Enable Daily Auto-Sync</h3>
            </div>
            <p className="text-sm text-succession-text-secondary mb-5 leading-relaxed">
              Your production data will be overwritten by the daily update and you will lose any manual changes you've made.
            </p>
            <div className="flex gap-2.5 justify-end">
              <button
                type="button"
                onClick={handleCancelAutoSync}
                className="px-3.5 py-1.5 text-sm border border-succession-border rounded-lg text-succession-text-primary hover:bg-succession-bg-primary transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmAutoSync}
                className="px-3.5 py-1.5 text-sm bg-succession-success text-white rounded-lg hover:opacity-80 transition-opacity inline-flex items-center gap-1.5"
              >
                Enable
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

RevenueInformationEditModal.propTypes = {
  user: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  title: PropTypes.string,
  submitButtonText: PropTypes.string,
  submitButtonIcon: PropTypes.node,
};

export default RevenueInformationEditModal;
